import React from "react";
import { Icon, Section, Eyebrow, Reveal } from "./primitives.jsx";

/* Make a click-handler div keyboard-operable (Enter/Space). */
export function cardKeys(onClick) {
  return {
    role: "button",
    tabIndex: 0,
    onKeyDown: (e) => {
      if (onClick && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onClick();
      }
    }
  };
}

export function SectionHead({ eyebrow, title, lede, onDark, align = "left", maxLede = 600, children }) {
  return (
    <div style={{ textAlign: align, maxWidth: align === "center" ? 760 : "none", margin: align === "center" ? "0 auto" : 0 }}>
      <Eyebrow onDark={onDark}>{eyebrow}</Eyebrow>
      <h2
        style={{
          margin: "14px 0 0",
          fontFamily: "var(--font-display)",
          fontWeight: 800,
          fontSize: "clamp(1.9rem, 3.4vw, var(--text-2xl))",
          lineHeight: 1.1,
          letterSpacing: "var(--tracking-tight)",
          textWrap: "balance",
          color: onDark ? "var(--neutral-0)" : "var(--brand-ink)"
        }}
      >
        {title}
      </h2>
      {lede && (
        <p
          style={{
            margin: align === "center" ? "16px auto 0" : "16px 0 0",
            maxWidth: maxLede,
            fontSize: "var(--text-md)",
            lineHeight: 1.65,
            color: onDark ? "var(--neutral-400)" : "var(--text-body)",
            textWrap: "pretty"
          }}
        >
          {lede}
        </p>
      )}
      {children}
    </div>
  );
}

export function IconTile({ name, tone = "red", radius = 12, size = 52 }) {
  const red = tone === "red";
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        flexShrink: 0,
        background: red ? "var(--red-50)" : "var(--neutral-100)",
        color: red ? "var(--brand-red)" : "var(--brand-ink)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Icon name={name} size={Math.round(size * 0.5)} strokeWidth={1.9} />
    </span>
  );
}

export function CTABand({ title, lede, button, onClick, radius = 22 }) {
  return (
    <Section pad="var(--space-10)">
      <Reveal>
        <div
          style={{
            background: "var(--brand-red)",
            borderRadius: radius + 6,
            padding: "clamp(2rem, 5vw, var(--space-10))",
            color: "#fff",
            boxShadow: "var(--shadow-brand)",
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--space-6)",
            alignItems: "center",
            justifyContent: "space-between",
            position: "relative",
            overflow: "hidden"
          }}
        >
          <div style={{ position: "absolute", right: -60, top: -60, width: 260, height: 260, borderRadius: 999, border: "40px solid rgba(255,255,255,0.08)" }} />
          <div style={{ position: "relative", maxWidth: 620 }}>
            <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(1.7rem, 3.2vw, var(--text-2xl))", lineHeight: 1.08, letterSpacing: "var(--tracking-tight)", color: "#fff", textWrap: "balance" }}>{title}</h2>
            <p style={{ margin: "12px 0 0", fontSize: "var(--text-md)", lineHeight: 1.6, color: "rgba(255,255,255,0.92)", textWrap: "pretty" }}>{lede}</p>
          </div>
          <button
            onClick={onClick}
            style={{
              position: "relative",
              flexShrink: 0,
              background: "#fff",
              color: "var(--brand-ink)",
              border: "none",
              cursor: "pointer",
              padding: "0 26px",
              height: "var(--control-h-lg)",
              borderRadius: radius < 12 ? "var(--radius-sm)" : "var(--radius-md)",
              fontFamily: "var(--font-sans)",
              fontWeight: 700,
              fontSize: "var(--text-md)",
              letterSpacing: "-0.01em",
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
              transition: "transform var(--duration-fast) var(--ease-out)"
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
          >
            {button}
            <Icon name="arrow-right" size={18} color="var(--brand-red)" />
          </button>
        </div>
      </Reveal>
    </Section>
  );
}

export function ProductCard({ p, radius = 16, onClick, cta = "Learn more" }) {
  return (
    <div
      onClick={onClick}
      {...cardKeys(onClick)}
      aria-label={p.title}
      style={{
        background: "var(--surface-card)",
        borderRadius: radius,
        padding: "var(--space-5)",
        boxShadow: "var(--shadow-sm)",
        border: "1px solid var(--border-subtle)",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        height: "100%",
        transition: "transform var(--duration-base) var(--ease-out), box-shadow var(--duration-base) var(--ease-out)"
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "var(--shadow-md)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "var(--shadow-sm)"; }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <IconTile name={p.icon} radius={Math.max(8, radius - 4)} size={46} />
        <span style={{ fontSize: "var(--text-2xs)", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-muted)", background: "var(--neutral-50)", padding: "5px 10px", borderRadius: "var(--radius-pill)" }}>{p.category}</span>
      </div>
      <div>
        <h3 style={{ margin: 0, fontSize: "var(--text-lg)", fontWeight: 700, letterSpacing: "-0.01em", color: "var(--brand-ink)" }}>{p.title}</h3>
        <p style={{ margin: "8px 0 0", fontSize: "var(--text-sm)", lineHeight: 1.6, color: "var(--text-muted)" }}>{p.description}</p>
      </div>
      <span style={{ marginTop: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: "var(--text-sm)", fontWeight: 700, color: "var(--brand-red)" }}>
        {cta} <Icon name="arrow-right" size={15} />
      </span>
    </div>
  );
}

export function ArticleCard({ a, radius = 16, label, onClick }) {
  const image = a.image || a.cover || "";
  return (
    <article
      onClick={onClick}
      {...cardKeys(onClick)}
      aria-label={a.title}
      style={{
        background: "var(--surface-card)",
        borderRadius: radius,
        overflow: "hidden",
        border: "1px solid var(--border-subtle)",
        boxShadow: "var(--shadow-sm)",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        height: "100%",
        transition: "transform var(--duration-base) var(--ease-out), box-shadow var(--duration-base) var(--ease-out)"
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "var(--shadow-md)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "var(--shadow-sm)"; }}
    >
      <div style={{ height: 160, background: "var(--brand-ink)", position: "relative", overflow: "hidden" }}>
        {image ? (
          <img
            src={image}
            alt=""
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transform: "scale(1.01)" }}
          />
        ) : (
          <div style={{ position: "absolute", inset: 0, opacity: 0.5, background: "repeating-linear-gradient(135deg, transparent 0 14px, rgba(255,255,255,0.04) 14px 15px)" }} />
        )}
        <div style={{ position: "absolute", inset: 0, background: image ? "linear-gradient(180deg, rgba(17,17,19,0.1), rgba(17,17,19,0.55))" : "transparent" }} />
        <span style={{ position: "absolute", left: 16, top: 16, fontSize: "var(--text-2xs)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#fff", background: "var(--brand-red)", padding: "5px 10px", borderRadius: "var(--radius-pill)" }}>{a.tag}</span>
        {!image && <Icon name="file-text" size={30} color="rgba(255,255,255,0.5)" style={{ position: "absolute", right: 16, bottom: 14 }} />}
      </div>
      <div style={{ padding: "var(--space-5)", display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ display: "flex", gap: 10, fontSize: "var(--text-xs)", color: "var(--text-muted)", marginBottom: 10 }}>
          <span>{a.date}</span>
          <span>·</span>
          <span>{a.read}</span>
        </div>
        <h3 style={{ margin: 0, fontSize: "var(--text-md)", fontWeight: 700, lineHeight: 1.3, letterSpacing: "-0.01em", color: "var(--brand-ink)", textWrap: "balance" }}>{a.title}</h3>
        <p style={{ margin: "10px 0 0", fontSize: "var(--text-sm)", lineHeight: 1.6, color: "var(--text-muted)", flex: 1 }}>{a.excerpt}</p>
        <span style={{ marginTop: 16, display: "inline-flex", alignItems: "center", gap: 6, fontSize: "var(--text-sm)", fontWeight: 700, color: "var(--brand-red)" }}>{label} <Icon name="arrow-right" size={15} /></span>
      </div>
    </article>
  );
}
